/**
 * The calendar invite card at the top of a message (contracts MailInvite, core logic/calendarInvite), Gmail style:
 * the date and time in my zone (the organizer's in grey when it differs), the title, organizer and guests, the meeting
 * link, what it overlaps in my calendar, and Yes / No / Maybe / Propose new time. Answered, updated, cancelled and
 * out-of-date invites get their own line; a repeating one asks "This event" or "All events" before answering.
 */
import type { AnswerMailInviteRequest, MailInvite } from "@bundle/contracts";
import { Shell } from "@bundle/core";
import { inviteCardState, zoneCity } from "@bundle/core/logic/calendarInvite";
import { Banner, Button, Card, Icon, IconButton, Menu, Pressable, RadioGroup, Stack, Text, openLink, useLocale, useNavigation, useT, useWorkspace, type MenuEntry } from "@bundle/ui";
import { useState } from "react";

type Answer = AnswerMailInviteRequest["response"];
type Scope = NonNullable<AnswerMailInviteRequest["scope"]>;

const ANSWERS: Answer[] = ["accepted", "declined", "tentative"];

/** "Thu, Oct 29 · 12:00 – 1:00 PM" in a zone (all-day: the dates). */
export function inviteWhen(inv: Pick<MailInvite, "start" | "end" | "allDay" | "timeZone">, locale: string, zone: string): string {
  if (inv.allDay) {
    const day = new Intl.DateTimeFormat(locale, { weekday: "short", month: "short", day: "numeric", timeZone: inv.timeZone });
    return inv.end - inv.start <= 86_400_000 ? day.format(inv.start) : day.formatRange(inv.start, inv.end - 1);
  }
  const day = new Intl.DateTimeFormat(locale, { weekday: "short", month: "short", day: "numeric", timeZone: zone });
  const time = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone: zone });
  if (day.format(inv.start) !== day.format(inv.end)) {
    const full = new Intl.DateTimeFormat(locale, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: zone });
    return full.formatRange(inv.start, inv.end);
  }
  return `${day.format(inv.start)} · ${time.formatRange(inv.start, inv.end)}`;
}

function timeRange(start: number, end: number, locale: string, zone: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone: zone }).formatRange(start, end);
}

type Props = {
  messageId: string;
  invite: MailInvite;
  /** My answer on its way (shown at once). */
  answering: Answer | undefined;
  failed: boolean;
  /** Absent while Email is look-only: the card shows the invite but can't answer it. */
  onAnswer?: ((response: Answer, scope?: Scope) => void) | undefined;
  onPropose?: (() => void) | undefined;
  onDownload: () => void;
};

export function MailInviteCard({ messageId, invite: inv, answering, failed, onAnswer, onPropose, onDownload }: Props) {
  const t = useT();
  const locale = useLocale();
  const nav = useNavigation();
  const ws = useWorkspace();
  const [changing, setChanging] = useState(false);
  const [asking, setAsking] = useState<{ response: Answer; scope: Scope } | undefined>(undefined);
  const myZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const shown = answering === undefined ? inv : { ...inv, myResponse: answering, movedFrom: undefined };
  const state = inviteCardState(shown);
  const cancelled = state === "cancelled";
  const when = inviteWhen(inv, locale, myZone);
  const theirs = inv.allDay ? when : timeRange(inv.start, inv.end, locale, inv.timeZone);
  const showTheirs = !inv.allDay && theirs !== timeRange(inv.start, inv.end, locale, myZone);
  const name = (g: { address: string; name?: string | undefined }) => g.name ?? g.address;
  const others = inv.guests.filter((g) => g.address !== inv.myAddress);
  const listed = others.slice(0, 3).map(name).join(", ");
  const more = inv.guestCount - (inv.guests.some((g) => g.address === inv.myAddress) ? 1 : 0) - Math.min(3, others.length);
  const guestNames = more > 0 ? `${listed} ${t("mail.invite.guestsMore", { count: more })}` : listed;
  const guests = inv.guests.some((g) => g.address === inv.myAddress) ? (guestNames === "" ? t("mail.me") : t("mail.invite.guestsWithYou", { names: guestNames })) : guestNames;
  const openCalendar = () => nav.open(Shell.formatUrl({ type: "calendar", ws: ws.slug, view: "week", at: inv.start, ...(inv.eventId === undefined ? {} : { event: inv.eventId }) }));
  const answer = (response: Answer) => {
    // A repeating event asks first (Google: this event or all of them); an invite for one occurrence is just that one.
    if (inv.recurring && inv.occurrence === undefined) setAsking({ response, scope: "occurrence" });
    else {
      setChanging(false);
      onAnswer?.(response);
    }
  };
  // Nothing of the mailbox can send yet (its domain still at Google): no answering here, the card says why.
  const notSetUp = state === "pending" || state === "updated" || state === "answered" ? inv.replyNotSetUp : undefined;
  const canReply = onAnswer !== undefined && notSetUp === undefined;
  const moreMenu: MenuEntry[] = [
    ...(inv.eventId === undefined ? [] : [{ id: "open", label: t("mail.invite.openCalendar"), icon: "calendar" as const, onSelect: openCalendar }]),
    ...(onPropose === undefined ? [] : [{ id: "propose", label: t("mail.invite.propose"), icon: "clock" as const, onSelect: onPropose }]),
    { id: "download", label: t("mail.invite.download"), icon: "download", onSelect: onDownload },
  ];
  const canAnswer = canReply && (state === "pending" || state === "updated" || (state === "answered" && changing));

  return (
    <Card padding="4" gap="3" testID={`mail-invite-${messageId}`}>
      <Stack direction="horizontal" align="center" gap="2" wrap>
        <Text weight="semibold" strike={cancelled} selectable testID={`mail-invite-when-${messageId}`}>
          {when}
        </Text>
        {showTheirs ? (
          <Text variant="footnote" color="text.secondary">
            {t("mail.invite.organizerTime", { time: theirs, city: zoneCity(inv.timeZone) })}
          </Text>
        ) : null}
      </Stack>
      <Text variant="title3" weight="bold" color={cancelled ? "text.secondary" : "text.primary"} strike={cancelled} selectable>
        {inv.title}
      </Text>
      {state === "updated" && inv.movedFrom !== undefined ? (
        <Stack direction="horizontal" align="center" gap="2" wrap testID={`mail-invite-moved-${messageId}`}>
          <Text color="text.secondary" strike>
            {inviteWhen({ ...inv, start: inv.movedFrom.start, end: inv.movedFrom.end }, locale, myZone)}
          </Text>
          <Icon name="arrowRight" size="icon.sm" color="text.tertiary" />
          <Text weight="semibold">{when}</Text>
        </Stack>
      ) : null}
      <Stack direction="horizontal" gap="5" wrap>
        <Stack direction="horizontal" gap="2.5" grow>
          <Icon name="users" size="icon.sm" color="text.secondary" />
          <Stack gap="0.5" grow>
            <Text selectable>{`${name(inv.organizer)} · ${t("mail.invite.organizer")}`}</Text>
            {guests === "" ? null : <Text color="text.secondary">{guests}</Text>}
            {inv.location !== undefined && inv.location !== inv.conferenceUrl ? <Text color="text.secondary" selectable>{inv.location}</Text> : null}
            {inv.conferenceUrl !== undefined && !cancelled ? (
              <Pressable onPress={() => openLink(inv.conferenceUrl!)} accessibilityLabel={t("mail.invite.join")} testID={`mail-invite-join-${messageId}`}>
                <Text variant="footnote" color="text.link">
                  {inv.conferenceUrl.replace(/^https?:\/\//, "").replace(/\?.*$/, "")}
                </Text>
              </Pressable>
            ) : null}
          </Stack>
        </Stack>
        {inv.method === "request" && state !== "superseded" ? (
          <Stack direction="horizontal" gap="2.5" grow>
            <Icon name="calendar" size="icon.sm" color={inv.conflicts.length > 0 ? "status.warning" : "text.secondary"} />
            <Stack gap="0.5" grow testID={`mail-invite-conflicts-${messageId}`}>
              <Text variant="footnote" weight="semibold" color="text.secondary">
                {t("mail.invite.inYourCalendar")}
              </Text>
              {inv.conflicts.length === 0 ? (
                <Text color="text.secondary">{t("mail.invite.noConflicts")}</Text>
              ) : (
                inv.conflicts.map((c) => (
                  <Text key={`${c.eventId}:${c.start}`} color="status.warning.fg">
                    {t("mail.invite.conflict", { title: c.title, time: timeRange(c.start, c.end, locale, myZone) })}
                  </Text>
                ))
              )}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
      {cancelled ? (
        <Stack gap="0.5" testID={`mail-invite-cancelled-${messageId}`}>
          <Text color="status.danger.fg">{t("mail.invite.cancelled")}</Text>
          <Text variant="footnote" color="text.secondary">
            {t("mail.invite.removed")}
          </Text>
        </Stack>
      ) : state === "superseded" ? (
        <Text color="text.secondary" testID={`mail-invite-superseded-${messageId}`}>
          {t("mail.invite.superseded")}
        </Text>
      ) : state === "answered" && !changing ? (
        <Stack gap="2" testID={`mail-invite-answered-${messageId}`}>
          <Stack direction="horizontal" align="center" gap="1.5" wrap>
            <Icon name={shown.myResponse === "declined" ? "close" : "check"} size="icon.sm" color={shown.myResponse === "accepted" ? "status.success" : "text.secondary"} />
            <Text weight="semibold" color={shown.myResponse === "accepted" ? "status.success.fg" : "text.primary"}>
              {t(`mail.invite.answered.${shown.myResponse as Answer}`)}
            </Text>
            <Text variant="footnote" color="text.secondary">
              {t("mail.invite.sentTo", { address: inv.myAddress })}
            </Text>
          </Stack>
          <Stack direction="horizontal" gap="2" wrap>
            {!canReply ? null : <Button variant="secondary" size="sm" label={t("mail.invite.change")} onPress={() => setChanging(true)} testID={`mail-invite-change-${messageId}`} />}
            {inv.eventId === undefined ? null : <Button variant="secondary" size="sm" label={t("mail.invite.openCalendar")} onPress={openCalendar} testID={`mail-invite-open-${messageId}`} />}
          </Stack>
        </Stack>
      ) : null}
      {state === "updated" && inv.myResponse !== "needs_action" ? (
        <Text variant="footnote" color="text.secondary">
          {t("mail.invite.answerKept", { answer: t(`calendar.rsvp.${inv.myResponse as Answer}`) })}
        </Text>
      ) : null}
      {canAnswer && asking === undefined ? (
        <Stack direction="horizontal" align="center" gap="2" wrap>
          <Text variant="footnote" color="text.secondary">
            {t("calendar.rsvp.question")}
          </Text>
          {ANSWERS.map((a) => (
            <Button
              key={a}
              variant={shown.myResponse === a || shown.myResponse === "needs_action" ? "primary" : "secondary"}
              icon={shown.myResponse === a ? "check" : undefined}
              label={t(`calendar.rsvp.${a}`)}
              loading={answering === a}
              onPress={() => answer(a)}
              testID={`mail-invite-${a}-${messageId}`}
            />
          ))}
          {onPropose === undefined ? null : <Button variant="secondary" label={t("mail.invite.propose")} onPress={onPropose} testID={`mail-invite-propose-${messageId}`} />}
          <Menu items={moreMenu} align="end" testID={`mail-invite-menu-${messageId}`}>
            <IconButton icon="more" accessibilityLabel={t("mail.invite.more")} onPress={() => undefined} testID={`mail-invite-more-${messageId}`} />
          </Menu>
        </Stack>
      ) : null}
      {asking !== undefined ? (
        <Stack gap="2.5" padding="3" radius="md" background="bg.sunken" testID={`mail-invite-scope-${messageId}`}>
          <Text weight="semibold">{t("calendar.scope.rsvpTitle")}</Text>
          <RadioGroup<Scope>
            value={asking.scope}
            onChange={(scope) => setAsking({ ...asking, scope })}
            options={[
              { value: "occurrence", label: t("calendar.scope.this") },
              { value: "series", label: t("calendar.scope.all") },
            ]}
            accessibilityLabel={t("calendar.scope.rsvpTitle")}
            testID={`mail-invite-scope-choice-${messageId}`}
          />
          <Stack direction="horizontal" gap="2" justify="end">
            <Button variant="secondary" label={t("common.cancel")} onPress={() => setAsking(undefined)} testID={`mail-invite-scope-cancel-${messageId}`} />
            <Button
              variant="primary"
              label={t("mail.compose.send")}
              onPress={() => {
                onAnswer?.(asking.response, asking.scope);
                setAsking(undefined);
                setChanging(false);
              }}
              testID={`mail-invite-scope-send-${messageId}`}
            />
          </Stack>
        </Stack>
      ) : null}
      {notSetUp === undefined ? null : <Banner tone="warning" message={t("mail.invite.replyNotSetUp", { domain: notSetUp })} testID={`mail-invite-not-set-up-${messageId}`} />}
      {failed ? <Banner tone="danger" icon="warning" message={t("mail.invite.answerFailed")} testID={`mail-invite-failed-${messageId}`} /> : null}
    </Card>
  );
}
